import Vue from 'vue'
import {getUserFullName, objectById, searchFieldsHaveValues} from 'src/utilities/utils'
import {make} from 'vuex-pathify'
import {byId, crudifyMutations, hydrateEdit, resetResults, savePayload} from 'src/utilities/vuex2'
import Constants from 'src/utilities/constants'
import {esClient, filterBuilder, multiMatch} from 'src/utilities/ptracSearch'
import {ptracGetEntities} from 'src/utilities/ptracApi'
import {OK} from 'http-status-codes'
import {createAndDownloadWorksheet} from '@/utilities/excel'
import {getCompanies} from '@/utilities/ptracApiHelper'
import _ from 'lodash'

const edit = {
  id: null,
  name: null,
  jobName: null,
  processDefinitionKey: null,
  departmentId: null,
  communityId: null,
  priority: null,
  clientName: null,
  clientId: null,
  clientContactId: null,
  builderName: null,
  builderId: null,
  builderContactId: null,
  planNameId: null,
  tasks: [],
  createdBy: null,
  createdAt: null,
  updatedBy: null,
  updatedAt: null
}

const query = {
  search: null,
  departmentIds: [],
  clientId: null,
  builderId: null,
}

const initialState = {
  byDepartment: [],
  query: JSON.parse(JSON.stringify(query)),
  edit: JSON.parse(JSON.stringify(edit)),
  results: []
}

const state = Vue.util.extend({}, JSON.parse(JSON.stringify(initialState)))

const getters = make.getters(state)
getters.byId = byId
getters.savePayload = savePayload

getters.kvpsForSelect = (state) => {
  const kvps = []
  state.byDepartment.forEach((jobTemplate) => {
    const kvp = {
      label: jobTemplate.name,
      value: jobTemplate.id
    }
    kvps.push(kvp)
  })

  return kvps
}

getters.searchFieldsHaveValues = (state) => {
  const searchFields = [
    'search',
    'clientId',
    'builderId',
    'departmentIds'
  ]

  return searchFieldsHaveValues(state.query, searchFields)
}

const mutations = make.mutations(state)
crudifyMutations(mutations)

mutations.hydrateEdit = hydrateEdit
mutations.resetResults = resetResults
mutations.resetEdit = (state) => {
  state.edit = JSON.parse(JSON.stringify(edit))
}
mutations.resetQuery = (state) => {
  state.query = JSON.parse(JSON.stringify(initialState.query))
}
mutations.taskAssignee = (state, payload) => {
  state.edit.tasks[payload.index].assigneeId = payload.assigneeId
  state.edit.tasks[payload.index].assigneeName = payload.assigneeName
}
mutations.taskDueDatePlusDays = (state, payload) => {
  state.edit.tasks.forEach(task => {
    if (task.nameId === payload.nameId) {
      task.dueDatePlusDays = payload.dueDatePlusDays
    }
  })
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions: {
    async fetchByDepartment({commit}, departmentId) {
      let url = 'job-templates/search/findByDepartmentIdOrderByNameAsc?departmentId=' + departmentId
      const response = await ptracGetEntities(url)

      if (OK === response.status) {
        commit('byDepartment', response.data)
      }

      return response
    },
    async fetchSearchResults({state, commit, rootGetters}) {
      const jobTemplateFields = ['name', 'jobName']

      const searchBody = {
        size: Constants.ELASTICSEARCH.MAX_SEARCH_RESULTS,
        query: {
          bool: {
            must: [
              {
                match: {
                  _class: Constants.ENTITY_FIELDS.JOB_TEMPLATE.CLASS
                }
              }
            ],
            should: [],
            filter: []
          }
        }
      }

      const filterFields = [
        'priority',
        'clientId',
        'builderId'
      ]

      // Basic Search
      if (state.query.search) {
        const splitSearchTerms = state.query.search.toLowerCase().trim().split(" ")
        searchBody.query.bool.must.push(multiMatch(jobTemplateFields, splitSearchTerms))
      }
      // Departments
      if (state.query.departmentIds.length > 0) {
        searchBody.query.bool.filter.push({match: {departmentId: state.query.departmentIds.join(" ")}})
      }
      // Filters
      searchBody.query.bool.filter.push(...filterBuilder(filterFields, state.query))

      const esCreds = rootGetters['config/byName'](Constants.CONFIGS.ELASTICSEARCH_UN_PW)
      let esResponse = await esClient(esCreds).search({
        index: 'ptrac',
        body: searchBody
      })

      commit('results', esResponse.hits.hits)
    },
    hydrateTaskAssignees({state, rootState}) {
      for (const ptracTask of state.edit.tasks) {
        ptracTask.assigneeName = null

        if (ptracTask.assigneeId) {
          const assignee = objectById(rootState.users.results, ptracTask.assigneeId)
          if (assignee) {
            ptracTask.assigneeName = getUserFullName(assignee)
          }
        }
      }
    },
    async fetchTasks({state, rootState, dispatch}, jobId) {
      const response = await ptracGetEntities('tasks/search/findByJobIdOrderByCreatedAtAsc?jobId=' + jobId, 'ptracTasks')

      if (OK === response.status) {
        const tasks = []

        for (const ptracTask of response.data) {
          tasks.push({
            name: ptracTask.name,
            nameId: ptracTask.nameId,
            assigneeName: null,
            assigneeId: ptracTask.assigneeId,
            dueDatePlusDays: 0
          })
        }

        state.edit.tasks = tasks

        dispatch('hydrateTaskAssignees')
      }
    },
    async resultsToExcel({state, rootState}) {
      const fileName = 'job-template-search-results.xlsx'
      const tabName = 'Job Templates'
      const header = [{
          header: 'Template Name',
          key: 'name',
          width: 30
        }, {
          header: 'Job Name',
          key: 'jobName',
          width: 30
        }, {
          header: 'Client',
          key: 'clientName',
          width: 30
        }, {
          header: 'Client Contact',
          key: 'clientContact',
          width: 30
        }, {
          header: 'Builder',
          key: 'builderName',
          width: 30
        }, {
          header: 'Builder Contact',
          key: 'builderContact',
          width: 30
        }]

      const jobTemplates = _.map(state.results, '_source')
      const companyMap = await getCompanies(jobTemplates, 'clientId', 'builderId')

      for (const jobTemplate of jobTemplates) {
        if (jobTemplate.clientId && jobTemplate.clientContactId) {
          const company = companyMap.get(jobTemplate.clientId)
          if (company) {
            const contact = company.contacts.find(contact => contact.id === jobTemplate.clientContactId)
            if (contact) {
              jobTemplate.clientContact = `${contact.firstName} ${contact.lastName}`
            }
          }
        }

        if (jobTemplate.builderId && jobTemplate.builderContactId) {
          const company = companyMap.get(jobTemplate.builderId)
          if (company) {
            const contact = company.contacts.find(contact => contact.id === jobTemplate.builderContactId)
            if (contact) {
              jobTemplate.builderContact = `${contact.firstName} ${contact.lastName}`
            }
          }
        }
      }

      const user = rootState.security.user

      createAndDownloadWorksheet(fileName, tabName, header, jobTemplates, user)
    }

  }
}
